import type { Config, DriveStep } from 'driver.js';

/**
 * A single attribute-based targeting rule.
 * Allows picking any attribute from the company or employee service.
 */
export interface TargetingRule {
  /** Unique identifier for this rule. */
  id: string;
  /** Which data source this attribute comes from. */
  source: 'company' | 'employee';
  /** The attribute name (e.g. 'id', 'department', 'employmentType'). */
  attribute: string;
  /** The value to compare against. */
  value: string;
}

/**
 * Targeting rules for determining which users should see a thought bubble flow.
 * Uses extensible attribute-based rules instead of hardcoded fields.
 */
export interface TargetingRules {
  /** List of attribute-based rules. Empty = all users. */
  rules: TargetingRule[];
  /** How to combine rules: 'all' = every rule must match, 'any' = at least one. Empty string when no rules. */
  matchMode: 'all' | 'any' | '';
  /** If true, only show to users on their first login. */
  firstLoginOnly: boolean;
}

/**
 * Whether a flow targets specific DOM elements or shows centered popovers.
 */
export type ThoughtBubbleFlowType = 'element' | 'no-element';

/**
 * A single step in a thought bubble flow.
 * Maps to a driver.js DriveStep but with additional metadata for admin authoring.
 */
export interface ThoughtBubbleStep {
  /** Server-assigned ID. Undefined for steps not yet persisted to the API. */
  id?: string;
  /** CSS selector for the target element. Empty/omitted for no-element flows. */
  elementSelector?: string;
  /** Title displayed in the popover. */
  title: string;
  /** Description/body text displayed in the popover. */
  description: string;
  /** Which side of the element to place the popover. */
  side: 'top' | 'right' | 'bottom' | 'left';
  /** Alignment of the popover relative to the element. */
  align: 'start' | 'center' | 'end';
  /** Whether to prevent user interaction with the highlighted element. */
  disableActiveInteraction: boolean;
  /** The order of this step in the flow (0-indexed). */
  order: number;
}

/**
 * Status of a thought bubble flow.
 */
export type ThoughtBubbleStatus = 'draft' | 'active' | 'disabled';

/**
 * Which application a thought bubble flow targets.
 */
export type ThoughtBubbleApp = 'admin' | 'employee';

/**
 * A complete thought bubble flow configuration.
 * Top-level entity that contains steps, targeting rules, and domain scoping.
 */
export interface ThoughtBubbleFlow {
  /** Unique identifier for this flow. */
  id: string;
  /** Whether this flow targets elements or shows centered popovers. */
  type: ThoughtBubbleFlowType;
  /** Which application this flow targets. */
  app: ThoughtBubbleApp;
  /** Human-readable name for the flow. */
  name: string;
  /** Description of what this flow explains. */
  description: string;
  /** Ember route name or URL pattern this flow targets. Supports wildcards (e.g. 'authenticated.companies.show.*'). */
  route: string;
  /** Ordered list of steps in this flow. */
  steps: ThoughtBubbleStep[];
  /** Rules for determining which users should see this flow. */
  targetingRules: TargetingRules;
  /** List of domains where this flow is active. Empty = all domains. */
  enabledDomains: string[];
  /** Current status of this flow. Only 'active' flows are shown to users. */
  status: ThoughtBubbleStatus;
  /** Display priority when multiple flows match the same route. 0 = highest priority (invoked first). */
  priority?: number;
  /** Number of recorded invocations. Undefined if API hasn't provided the count yet. */
  invocationsCount?: number;
  /**
   * Number of invocations scoped to a specific company.
   * Only populated when filter[company_id] is sent; may be null when no company filter is applied.
   */
  companyInvocationsCount?: number | null;
  /** ISO 8601 timestamp of when this flow was created. */
  createdAt: string;
  /** ISO 8601 timestamp of when this flow was last updated. */
  updatedAt: string;
}

/**
 * Convert a ThoughtBubbleStep to a driver.js DriveStep.
 */
export function toDriverStep(step: ThoughtBubbleStep): DriveStep {
  const driveStep: DriveStep = {
    popover: {
      title: step.title,
      description: step.description,
      side: step.side,
      align: step.align,
    },
    disableActiveInteraction: step.disableActiveInteraction,
  };
  if (step.elementSelector) {
    driveStep.element = step.elementSelector;
  }
  return driveStep;
}

/**
 * Convert a ThoughtBubbleFlow to a driver.js Config (partial).
 * Used by the runtime service to delegate to the existing tour manager.
 */
export function toDriverConfig(flow: ThoughtBubbleFlow): Partial<Config> {
  return {
    showProgress: flow.steps.length > 1,
    showButtons: flow.steps.length > 1 ? ['next', 'previous', 'close'] : ['close'],
    steps: flow.steps.sort((a, b) => a.order - b.order).map(toDriverStep),
  };
}

/**
 * Create a default empty ThoughtBubbleFlow with sensible defaults.
 */
export function createEmptyFlow(overrides?: Partial<ThoughtBubbleFlow>): ThoughtBubbleFlow {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    type: 'element',
    app: 'admin',
    name: '',
    description: '',
    route: '',
    steps: [],
    targetingRules: {
      rules: [],
      matchMode: '',
      firstLoginOnly: false,
    },
    enabledDomains: [],
    status: 'draft',
    createdAt: now,
    updatedAt: now,
    ...overrides,
  };
}

/**
 * Create a default empty ThoughtBubbleStep.
 */
export function createEmptyStep(order: number, includeSelector = true): ThoughtBubbleStep {
  return {
    ...(includeSelector ? { elementSelector: '' } : {}),
    title: '',
    description: '',
    side: 'bottom',
    align: 'center',
    disableActiveInteraction: false,
    order,
  };
}
